/**
 * "Continue with Microsoft" — one component, both auth screens (ADR 0093).
 *
 * ## It renders only where it can work
 *
 * The project is asked which external providers are enabled before anything is offered
 * (`externalProviders()`), so a deployment whose Microsoft provider is not configured shows no
 * button at all rather than one that answers "Unsupported provider" when clicked. Three states,
 * and the middle one matters: while the answer is outstanding NOTHING renders, because a button
 * that appears a beat after the page would move the password form under somebody's cursor.
 *
 * ## Sign in and sign up are the same call
 *
 * `signInWithOAuth` creates the account if there is none, which is what a person means by
 * "continue with Microsoft" on either screen. So this component takes no mode and the two screens
 * differ only in the sentence above it.
 *
 * ## Where it sends them back to
 *
 * `redirectTo` is this site's own `/app`, built from `window.location.origin` so a preview
 * deployment returns to itself rather than to production. Supabase refuses a redirect that is not
 * on the project's allow-list — that refusal is what stops this being an open redirect — and the
 * failure sentence names the fix rather than reading as a fault of the person signing in.
 *
 * The mark is Microsoft's four-square logo, drawn here as four rects in their published colours.
 * It is their trademark used to name their sign-in, which is what the button does and all it does.
 */
import { useEffect, useState } from 'react'
import { Button } from '@flowza/ui'
import {
  MICROSOFT_LABEL,
  MICROSOFT_PROVIDER,
  MICROSOFT_SCOPES,
  describeMicrosoftFailure,
} from '@flowza/core/sso/microsoft'
import { externalProviders, getClient } from '../../supabase'

/** Microsoft's mark, at text size. Four squares, their colours, no wordmark. */
/** The API's own sentence for `social_login_disabled`, repeated so the two never disagree. */
export const SOCIAL_LOGIN_DISABLED =
  'Sign in with your email and password, or through your organization’s single sign-on'

function MicrosoftMark() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <rect x="0" y="0" width="7" height="7" fill="#F25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7FBA00" />
      <rect x="0" y="9" width="7" height="7" fill="#00A4EF" />
      <rect x="9" y="9" width="7" height="7" fill="#FFB900" />
    </svg>
  )
}

export function MicrosoftButton({
  intro,
  disabled = false,
}: {
  /** The sentence above the button. Sign-in and sign-up say it differently. */
  readonly intro: string
  readonly disabled?: boolean
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  // ADR 0106: the API refused a social session (`social_login_disabled`) after the redirect came
  // back, and `api/client.ts` sent the browser here with the reason in the address. The sentence
  // is the same one the API answers, shown where the person will try again. Read from the address
  // itself rather than the router: the client arrives here by a full navigation, and this button
  // is rendered outside a router by its own tests.
  const refused =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('refused') === 'social_login_disabled'

  useEffect(() => {
    let cancelled = false
    externalProviders()
      .then((providers) => {
        if (!cancelled)
          setEnabled(providers === null ? false : providers[MICROSOFT_PROVIDER] === true)
      })
      .catch(() => {
        if (!cancelled) setEnabled(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Not yet known, or known to be off: nothing. See the header — this is deliberate in both cases.
  if (enabled !== true) return null

  const onClick = async () => {
    const client = getClient()
    if (client === null) return
    setBusy(true)
    setFailure(null)
    try {
      const { error } = await client.auth.signInWithOAuth({
        provider: MICROSOFT_PROVIDER as 'azure',
        options: {
          scopes: MICROSOFT_SCOPES,
          redirectTo: `${window.location.origin}/app`,
        },
      })
      // On success the browser is already navigating to Microsoft, so there is nothing to do here
      // and no state worth setting: this component is about to be unmounted by the redirect.
      if (error) setFailure(describeMicrosoftFailure(error.message))
    } catch (error: unknown) {
      setFailure(describeMicrosoftFailure(error instanceof Error ? error.message : null))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-border-subtle mt-5 border-t pt-5">
      <p className="text-text-muted m-0 mb-3 text-sm">{intro}</p>
      <Button
        variant="secondary"
        onClick={() => void onClick()}
        disabled={disabled || busy}
        className="flex w-full items-center justify-center gap-2"
      >
        <MicrosoftMark />
        {busy ? 'Taking you to Microsoft…' : `Continue with ${MICROSOFT_LABEL}`}
      </Button>
      <p className="text-text-muted m-0 mt-2 text-xs">
        We ask Microsoft for your name and email address, and nothing else. Your files and your
        mailbox are separate connections you make later, if you want them.
      </p>
      {refused && failure === null ? (
        <p className="text-danger m-0 mt-3 text-sm" role="alert">
          {SOCIAL_LOGIN_DISABLED}
        </p>
      ) : null}
      {failure !== null ? (
        <p className="text-danger m-0 mt-3 text-sm" role="alert">
          {failure}
        </p>
      ) : null}
    </div>
  )
}
